import Keycloak from 'keycloak-js';

import type { PublicSettingsDTO } from '@nora-monorepo/nora-api-client';

import { useAuthStore } from '#ui-shared/stores/auth-store';
import { useConfigStore } from '#ui-shared/stores/config-store';

export async function bootstrapKeycloak(isInWebView = false): Promise<void> {
  if (isInWebView) {
    // In webview mode we never initialize Keycloak: the native layer handles authentication. This
    // covers real devices and the browser-based Native Layer Simulator, whose parent window acts
    // as the native layer and owns auth.
    return;
  }

  const keycloak = await setupKeycloak();

  const authStore = useAuthStore();

  authStore.setupTokenRefreshTimer(keycloak);
}

async function setupKeycloak(): Promise<Keycloak> {
  const configStore = useConfigStore();
  const authStore = useAuthStore();

  const publicConfig = configStore.publicConfig;

  if (!isValidConfig(publicConfig)) {
    throw new Error('Invalid public configuration for Keycloak.');
  }

  const keycloak = new Keycloak({
    url: publicConfig.keycloak.url,
    realm: publicConfig.keycloak.realm,
    clientId: publicConfig.keycloak.clientId,
  });

  const mobileAccessToken
    = localStorage.getItem('X-Mobile-Access-Token') ?? undefined;

  const auth = await keycloak.init({
    onLoad: 'login-required',
    checkLoginIframe: false,
    token: mobileAccessToken,
    responseMode: 'query',
  });

  if (!auth) {
    window.location.reload();

    return keycloak;
  }

  if (!keycloak || !keycloak.token || !keycloak.tokenParsed) {
    throw new Error('No keycloak token returned.');
  }

  authStore.keycloak = keycloak;
  authStore.updateToken(keycloak.token);

  return keycloak;
}

function isValidConfig(publicConfig: PublicSettingsDTO | undefined): publicConfig is PublicSettingsDTO {
  if (!publicConfig) {
    return false;
  }

  if (!publicConfig.keycloak) {
    return false;
  }

  if (!publicConfig.keycloak.url) {
    return false;
  }

  if (!publicConfig.keycloak.realm) {
    return false;
  }

  if (!publicConfig.keycloak.clientId) {
    return false;
  }

  return true;
}
